import {
    Box,
    Tooltip,
    TooltipProps,
    Typography,
    styled,
    tooltipClasses,
    Theme,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        borderRadius: '8px',
    },
}));

const getEvolutionColor = (evolutionValue?: number) => (theme: Theme) => {
    if (!evolutionValue) return theme.palette.darkGrey;
    return evolutionValue > 0 ? theme.palette.red : theme.palette.green;
};

const getFormattedEvolution = (evolutionValue?: number): string | undefined => {
    if (!evolutionValue) return undefined;
    return evolutionValue > 0
        ? `+${evolutionValue.toFixed(2)}%`
        : `${evolutionValue.toFixed(2)}%`;
};

type Props = {
    evolutionValue?: number;
    year: number;
    children: React.ReactElement;
    isMonthly: boolean;
};

const SimulationEvolutionTooltip = ({
    evolutionValue,
    year,
    isMonthly,
    children,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const formattedEvolution = getFormattedEvolution(evolutionValue);
    const evolutionColorGetter = getEvolutionColor(evolutionValue);

    return (
        <LightTooltip
            title={
                <Box
                    sx={{
                        display: 'inline-box',
                    }}
                >
                    {isMonthly ? (
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.darkGrey,
                            })}
                        >
                            {t(
                                'humanResources.simulation.monthlyEvolutionFromSimulated',
                                {
                                    year,
                                },
                            )}
                        </Typography>
                    ) : (
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.darkGrey,
                            })}
                        >
                            {t(
                                'humanResources.simulation.yearlyEvolutionFromSimulated',
                                {
                                    fieldCompared: 'réalisé',
                                    year: year - 1,
                                },
                            )}
                        </Typography>
                    )}
                    <Typography
                        sx={(theme) => ({
                            color: evolutionColorGetter(theme),
                        })}
                    >
                        {formattedEvolution}
                    </Typography>
                </Box>
            }
            followCursor
        >
            {children}
        </LightTooltip>
    );
};

export default SimulationEvolutionTooltip;
